import classNames from 'classnames/bind';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { hasFeatureEnabled } from '../../../services/feature';
import { get, getStatic } from '../../../services/local-storage';
import Text from '../../shared/_deprecated/elements/Text/Text';
import styles from './TripDebugPanel.scss';

const cx = classNames.bind(styles);

const TripDebugPanel = ({ currentPosition, activeTrip, currentDistance }) => {
  const showingTripDebugPanel = hasFeatureEnabled('trip-debug-active');
  const [panelMinified, setPanelMinified] = useState(false);
  const [distanceStats, setDistanceStats] = useState(null);
  const [lastPositionStats, setLastPositionStats] = useState(null);
  const [prevPositionStats, setPrevPositionStats] = useState(null);
  const [derivedStats, setDerivedStats] = useState({});

  useEffect(() => {
    const lastRecordedPosition = getStatic('logTrips', 'lastRecordedPosition');
    const prevRecordedPosition = getStatic('logTrips', 'prevRecordedPosition');
    const lastRecordedDistance = getStatic('logTrips', 'lastRecordedDistance');
    const debugStats = getStatic('logTrips', 'debugStats');

    const lastRecordedStats = {
      timestamp: lastRecordedPosition?.timestamp,
      coords: lastRecordedPosition?.coords,
      isMoving: lastRecordedPosition?.is_moving,
      activity: lastRecordedPosition?.activity,
      odometer: lastRecordedPosition?.odometer,
      battery: lastRecordedPosition?.battery,
    };

    const prevRecordedStats = {
      timestamp: prevRecordedPosition?.timestamp,
      coords: prevRecordedPosition?.coords,
      isMoving: prevRecordedPosition?.is_moving,
      activity: prevRecordedPosition?.activity,
      odometer: prevRecordedPosition?.odometer,
      battery: prevRecordedPosition?.battery,
    };

    const currentDistanceStats = {
      lastRecorded: lastRecordedDistance,
      total: currentDistance,
    };

    setLastPositionStats(lastRecordedStats);
    setPrevPositionStats(prevRecordedStats);
    setDistanceStats(currentDistanceStats);
    setDerivedStats(debugStats);
  }, [currentPosition, currentDistance]);

  const lastRecordedTime = DateTime.fromISO(lastPositionStats?.timestamp);
  const lastRecordedTimeDisplay = lastRecordedTime.toFormat(
    'cccc d LLLL - H:mm:ss',
  );
  const prevRecordedTime = DateTime.fromISO(lastPositionStats?.timestamp);
  const prevRecordedTimeDisplay = prevRecordedTime.toFormat(
    'cccc d LLLL - H:mm:ss',
  );

  const togglePanelMinify = () => {
    setPanelMinified(!panelMinified);
  };

  if (!showingTripDebugPanel || !activeTrip) {
    return null;
  }

  return (
    <div
      className={cx(
        `trip-debug-panel ${panelMinified ? 'trip-debug-panel--minified' : ''}`,
      )}
      style={{ marginBottom: '0' }}
    >
      <div
        className={cx('trip-debug-panel__button')}
        onClick={togglePanelMinify}
        onKeyDown={togglePanelMinify}
        type="button"
        role="link"
        tabIndex={0}
      >
        {panelMinified ? 'D' : 'X'}
      </div>

      <div className={cx('trip-debug-panel__content')}>
        <div className={cx('trip-debug-panel__section')}>
          <Text
            className={cx('trip-debug-panel__heading')}
            color="alternate"
            strong
            size="-3"
          >
            LAST RECORDED POSITION
          </Text>
          <Text
            className={cx('trip-debug-panel__stat')}
            color="alternate"
            size="-1"
          >
            {`heading: ${lastPositionStats?.coords?.heading} (${
              lastPositionStats?.coords?.heading_accuracy || '-'
            }) ${
              derivedStats?.heading
                ? ` | ${derivedStats?.heading.toFixed(2)}`
                : ''
            }`}
          </Text>
          <Text
            className={cx('trip-debug-panel__stat')}
            color="alternate"
            size="-1"
          >
            {`speed: ${lastPositionStats?.coords?.speed} (${
              lastPositionStats?.coords?.speed_accuracy || '-'
            })${
              derivedStats?.speed ? ` | ${derivedStats?.speed.toFixed(2)}` : ''
            }`}
          </Text>
          <Text
            className={cx('trip-debug-panel__stat')}
            color="alternate"
            size="-1"
          >
            {`Position: ${lastPositionStats?.coords?.longitude}, ${
              lastPositionStats?.coords?.latitude
            } (${lastPositionStats?.coords?.accuracy || '-'})`}
          </Text>
          <Text
            className={cx('trip-debug-panel__stat')}
            color="alternate"
            size="-1"
          >
            Time: {lastRecordedTimeDisplay}
          </Text>
          <Text
            className={cx('trip-debug-panel__stat')}
            color="alternate"
            size="-1"
          >
            Is moving: {lastPositionStats?.isMoving ? 'true' : 'false'}
          </Text>
        </div>

        <div className={cx('trip-debug-panel__section')}>
          <Text
            className={cx('trip-debug-panel__heading')}
            color="alternate"
            strong
            size="-3"
          >
            PREVIOUS RECORDED POSITION
          </Text>
          <Text
            className={cx('trip-debug-panel__stat')}
            color="alternate"
            size="-1"
          >
            {`heading: ${prevPositionStats?.coords?.heading} (${
              prevPositionStats?.coords?.heading_accuracy || '-'
            })`}
          </Text>
          <Text
            className={cx('trip-debug-panel__stat')}
            color="alternate"
            size="-1"
          >
            {`speed: ${prevPositionStats?.coords?.speed} (${
              prevPositionStats?.coords?.speed_accuracy || '-'
            })`}
          </Text>
          <Text
            className={cx('trip-debug-panel__stat')}
            color="alternate"
            size="-1"
          >
            {`Position: ${prevPositionStats?.coords?.longitude}, ${
              prevPositionStats?.coords?.latitude
            } (${prevPositionStats?.coords?.accuracy || '-'})`}
          </Text>
          <Text
            className={cx('trip-debug-panel__stat')}
            color="alternate"
            size="-1"
          >
            Time: {prevRecordedTimeDisplay}
          </Text>
          <Text
            className={cx('trip-debug-panel__stat')}
            color="alternate"
            size="-1"
          >
            Is moving: {prevPositionStats?.isMoving ? 'true' : 'false'}
          </Text>
        </div>

        <div className={cx('trip-debug-panel__section')}>
          <Text
            className={cx('trip-debug-panel__heading')}
            color="alternate"
            strong
            size="-3"
          >
            DISTANCE
          </Text>
          <Text
            className={cx('trip-debug-panel__stat')}
            color="alternate"
            size="-1"
          >
            {`Last recorded distance: ${distanceStats?.lastRecorded || '-'}`}
          </Text>
          <Text
            className={cx('trip-debug-panel__stat')}
            color="alternate"
            size="-1"
          >
            {`Total distance: ${distanceStats?.total || '-'}`}
          </Text>
        </div>
      </div>
    </div>
  );
};

TripDebugPanel.propTypes = {
  currentPosition: PropTypes.shape({}).isRequired,
  activeTrip: PropTypes.bool.isRequired,
  currentDistance: PropTypes.number.isRequired,
};

const mapStateToProps = (state) => ({
  currentPosition: state.geoService.currentPosition,
  activeTrip: state.logTrips.activeTrip,
  currentDistance: state.logTrips.currentDistance,
});

export default connect(mapStateToProps, {})(TripDebugPanel);
